import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Mail, MessageCircle, Copy, Trash2, Edit, Eye, Search, Sparkles, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import WhatsAppTemplatesPanel from '@/components/templates/WhatsAppTemplatesPanel';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CATEGORIES = [
  { id: 'marketing', label: 'Marketing', color: 'purple' },
  { id: 'transactional', label: 'Transactional', color: 'blue' },
  { id: 'newsletter', label: 'Newsletter', color: 'emerald' },
  { id: 'custom', label: 'Custom', color: 'slate' },
];

const TemplatesHubPage = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('email');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [newCategory, setNewCategory] = useState('marketing');

  // Starter templates ("Start from Template" picker).
  const [starters, setStarters] = useState([]);
  const [startersLoaded, setStartersLoaded] = useState(false);
  const [seedingStarter, setSeedingStarter] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return iso; }
  };

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/email-templates`, { headers });
      setTemplates(r.data.templates || []);
    } catch (e) { toast.error('Failed to load templates'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { if (tab === 'email') fetchTemplates(); }, [tab, fetchTemplates]);

  // Lazy-load starter catalogue when the user switches to Email tab.
  useEffect(() => {
    if (tab !== 'email' || startersLoaded) return;
    axios.get(`${API}/email-templates/starters`, { headers })
      .then((r) => { setStarters(r.data?.starters || []); setStartersLoaded(true); })
      .catch(() => { /* non-fatal — picker just won't show options */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, startersLoaded]);

  const seedFromStarter = async (key) => {
    if (!key || seedingStarter) return;
    setSeedingStarter(key);
    try {
      const r = await axios.post(`${API}/email-templates/starters/${key}/seed`, {}, { headers });
      const tpl = r.data?.template;
      toast.success(`Created "${tpl?.name}" — opening editor…`);
      navigate(`/marketing/templates/email/${tpl.id}`);
    } catch (e) {
      toast.error('Failed to create starter: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSeedingStarter('');
    }
  };

  const createTemplate = async () => {
    if (!newName.trim()) { toast.error('Name is required'); return; }
    try {
      const r = await axios.post(`${API}/email-templates`, {
        name: newName,
        subject: newSubject || newName,
        category: newCategory,
        body_html: '',
        design_json: null,
      }, { headers });
      toast.success('Template created');
      setNewOpen(false);
      setNewName(''); setNewSubject(''); setNewCategory('marketing');
      navigate(`/marketing/templates/email/${r.data.template.id}`);
    } catch (e) { toast.error('Create failed: ' + (e?.response?.data?.detail || e.message)); }
  };

  const duplicate = async (id) => {
    try {
      const r = await axios.post(`${API}/email-templates/${id}/duplicate`, {}, { headers });
      toast.success('Duplicated');
      fetchTemplates();
      navigate(`/marketing/templates/email/${r.data.template.id}`);
    } catch (e) { toast.error('Duplicate failed'); }
  };

  const del = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await axios.delete(`${API}/email-templates/${id}`, { headers });
      toast.success('Deleted');
      fetchTemplates();
    } catch (e) { toast.error('Delete failed'); }
  };

  const filtered = templates.filter(t => {
    if (categoryFilter !== 'all' && t.category !== categoryFilter) return false;
    if (search && !t.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="p-4 md:p-6 space-y-6" data-testid="templates-hub">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-amber-500/30 to-pink-500/30 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-amber-300" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold text-white">Templates</h1>
            <p className="text-sm text-gray-400">Reusable Email and WhatsApp templates for Broadcasts, Campaigns and Journeys.</p>
          </div>
        </div>
        {tab === 'email' && (
          <div className="flex items-center gap-2">
            {/* Start from Template — picker dropdown of the 5
                pre-built starters (Welcome, Order Confirmation,
                Out for Delivery, Delivered, Abandoned Cart). */}
            <Select value="" onValueChange={seedFromStarter}>
              <SelectTrigger
                className="w-48 bg-wt-bg border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
                data-testid="start-from-template-picker"
                disabled={!!seedingStarter}
              >
                <SelectValue placeholder={seedingStarter ? 'Creating…' : '✨ Start from Template'} />
              </SelectTrigger>
              <SelectContent>
                {starters.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500">No starters loaded yet.</div>
                ) : starters.map(s => (
                  <SelectItem
                    key={s.key}
                    value={s.key}
                    data-testid={`starter-option-${s.key}`}
                  >
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setNewOpen(true)} className="bg-gradient-to-r from-amber-500 to-pink-500 hover:opacity-90" data-testid="new-email-template-btn">
              <Plus className="w-4 h-4 mr-1" /> New Email Template
            </Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-wt-surface border border-wt-border">
          <TabsTrigger value="email" className="data-[state=active]:bg-amber-500/20" data-testid="tab-email">
            <Mail className="w-4 h-4 mr-1" /> Email Templates
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="data-[state=active]:bg-green-500/20" data-testid="tab-whatsapp">
            <MessageCircle className="w-4 h-4 mr-1" /> WhatsApp Templates
          </TabsTrigger>
        </TabsList>

        <TabsContent value="email" className="mt-4 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search templates..." className="pl-9 bg-wt-bg" />
            </div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-44 bg-wt-bg"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={fetchTemplates}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center border border-dashed border-wt-border rounded-lg">
              <Mail className="w-10 h-10 mx-auto text-gray-500 mb-3" />
              <div className="text-white font-medium">{search || categoryFilter !== 'all' ? 'No templates match your filters' : 'No email templates yet'}</div>
              <div className="text-sm text-gray-500 mt-1">
                {search || categoryFilter !== 'all' ? 'Try clearing your search or filter.' : 'Create your first template and design it with the drag-and-drop editor.'}
              </div>
              {!search && categoryFilter === 'all' && (
                <Button onClick={() => setNewOpen(true)} className="mt-4 bg-gradient-to-r from-amber-500 to-pink-500">
                  <Plus className="w-4 h-4 mr-1" /> Create Email Template
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map(t => (
                <div key={t.id} className="bg-wt-surface border border-wt-border rounded-lg overflow-hidden hover:border-amber-500/50 transition group" data-testid={`tpl-card-${t.id}`}>
                  <div className="aspect-[16/10] bg-gradient-to-br from-wt-paper to-wt-bg flex items-center justify-center border-b border-wt-border relative">
                    {t.thumbnail_url ? (
                      <img src={t.thumbnail_url} alt={t.name} className="w-full h-full object-cover" />
                    ) : (
                      <Mail className="w-10 h-10 text-gray-600" />
                    )}
                    <Badge variant="outline" className="absolute top-2 left-2 text-xs bg-wt-bg/70">{t.category}</Badge>
                  </div>
                  <div className="p-3">
                    <div className="font-semibold text-white truncate">{t.name}</div>
                    <div className="text-xs text-gray-500 truncate mt-0.5">{t.subject || '(no subject)'}</div>
                    <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                      <span>{t.usage_count || 0} uses</span>
                      <span>{fmtDate(t.updated_at)}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-3 opacity-0 group-hover:opacity-100 transition">
                      <Button size="sm" variant="outline" className="flex-1" onClick={() => navigate(`/marketing/templates/email/${t.id}`)} data-testid={`edit-tpl-${t.id}`}>
                        <Edit className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => duplicate(t.id)} title="Duplicate">
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => del(t.id, t.name)} title="Delete" data-testid={`delete-tpl-${t.id}`}>
                        <Trash2 className="w-4 h-4 text-red-400" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="whatsapp" className="mt-4">
          <WhatsAppTemplatesPanel />
        </TabsContent>
      </Tabs>

      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent className="bg-wt-surface border-wt-border">
          <DialogHeader><DialogTitle className="text-white">New Email Template</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white">Name</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g., Welcome Email" className="bg-wt-bg mt-1" data-testid="new-tpl-name" />
            </div>
            <div>
              <Label className="text-white">Subject (can include {'{{first_name}}'} variables)</Label>
              <Input value={newSubject} onChange={(e) => setNewSubject(e.target.value)} placeholder="e.g., Welcome {{first_name}}!" className="bg-wt-bg mt-1" />
            </div>
            <div>
              <Label className="text-white">Category</Label>
              <Select value={newCategory} onValueChange={setNewCategory}>
                <SelectTrigger className="bg-wt-bg mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewOpen(false)}>Cancel</Button>
            <Button onClick={createTemplate} className="bg-gradient-to-r from-amber-500 to-pink-500" data-testid="submit-new-tpl">Create & Open Editor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TemplatesHubPage;
